import { useMemo } from 'react';
import { useGetUserTablePermissionsQuery } from '../services/api/user.api';
import { TABLE_IDS, PERMISSION_TYPES } from '../utils/constants';
import { useAppSelector } from '../services/hooks';
import { userSelector } from '../services/selectors/user.selectors';
import { IUserAppTableRule } from '../types/types';

// Lower number = higher priority for global (non-user-specific) scope rules.
// All Therapists (4) is more specific than All Company (2) for therapist users.
const SCOPE_TYPE_PRIORITY: Record<number, number> = {
  1: 1, // User Specific (handled separately via usersId, included for completeness)
  4: 2, // All Therapists
  2: 3, // All Company
  3: 4, // All Admins (lowest priority for therapist users) (no server return)
};

export const useTablePermissions = () => {
  const user = useAppSelector(userSelector);

  const userId: number = typeof user.id === 'string' ? parseInt(user.id, 10) : Number(user.id);

  const { data: permissions, isLoading, isError } = useGetUserTablePermissionsQuery(undefined,
    { skip: !user.id || isNaN(userId) }
  );

  /**
   * Resolves the single effective permission rule for a table from all active rules.
   *
   * Priority:
   *   1. User-specific rule (usersId === current user) — picks the most permissive if multiple
   *   2. Global rules (usersId === null) — picks the highest-priority scope:
   *      All Therapists (4) > All Company (2) > All Admins (3)
   */
  const resolveEffectivePermission = useMemo(() => {
    return (tablePermissions: IUserAppTableRule[]): IUserAppTableRule | null => {
      if (tablePermissions.length === 0) return null;

      // User-specific rules take top priority
      const userSpecificRules = tablePermissions.filter(p => p.usersId === userId);
      if (userSpecificRules.length > 0) {
        return userSpecificRules.reduce((best, current) => {
          if (current.permissionId === PERMISSION_TYPES.EDIT) return current;
          if (best.permissionId === PERMISSION_TYPES.EDIT) return best;
          if (current.permissionId === PERMISSION_TYPES.READ_ONLY) return current;
          return best;
        });
      }

      // Global rules: pick by scope priority
      const globalRules = tablePermissions.filter(p => p.usersId === null);
      if (globalRules.length === 0) return null;

      return globalRules.reduce((best, current) => {
        const bestPriority = SCOPE_TYPE_PRIORITY[best.scopeTypeId ?? 99] ?? 99;
        const currentPriority = SCOPE_TYPE_PRIORITY[current.scopeTypeId ?? 99] ?? 99;
        return currentPriority < bestPriority ? current : best;
      });
    };
  }, [userId]);

  const hasPermission = useMemo(() => {
    return (tableId: number): boolean => {
      if (isLoading) return false;
      if (isError || !permissions || permissions.length === 0) return true;

      const tablePermissions = permissions.filter(
        (p: IUserAppTableRule) => p.appTableId === tableId && p.statusId === 1
      );

      if (tablePermissions.length === 0) return true;

      const permission = resolveEffectivePermission(tablePermissions);
      if (!permission) return true;

      return permission.permissionId !== PERMISSION_TYPES.NO_PERMISSION;
    };
  }, [permissions, isLoading, isError, resolveEffectivePermission]);

  const isReadOnly = useMemo(() => {
    return (tableId: number): boolean => {
      if (isLoading) return true;
      if (isError || !permissions || permissions.length === 0) return false;

      const tablePermissions = permissions.filter(
        (p: IUserAppTableRule) => p.appTableId === tableId && p.statusId === 1
      );

      if (tablePermissions.length === 0) return false;

      const permission = resolveEffectivePermission(tablePermissions);
      if (!permission) return false;

      return permission.permissionId === PERMISSION_TYPES.READ_ONLY;
    };
  }, [permissions, isLoading, isError, resolveEffectivePermission]);

  const canEdit = useMemo(() => {
    return (tableId: number): boolean => {
      if (isLoading) return false;
      if (isError || !permissions || permissions.length === 0) return true;

      const tablePermissions = permissions.filter(
        (p: IUserAppTableRule) => p.appTableId === tableId && p.statusId === 1
      );

      if (tablePermissions.length === 0) return true;

      const permission = resolveEffectivePermission(tablePermissions);
      if (!permission) return true;

      return permission.permissionId === PERMISSION_TYPES.EDIT;
    };
  }, [permissions, isLoading, isError, resolveEffectivePermission]);

  return {
    hasPermission,
    isReadOnly,
    canEdit,
    permissions,
    isLoading,
    isError,
  };
};
